'use client';

import { type ReactElement, useState } from 'react';
import { Eye, EyeOff } from 'lucide-react';

import { Icon } from '../Icon/Icon';
import { Input, type InputProps } from '../Input/Input';

import styles from './PasswordInput.module.scss';

export interface PasswordInputProps extends Omit<InputProps, 'type' | 'right'> {
    /** Accessible name of the toggle while the password is hidden, e.g. "Show password" (translated). */
    showPasswordLabel: string;
    /** Accessible name of the toggle while the password is shown, e.g. "Hide password" (translated). */
    hidePasswordLabel: string;
}

/**
 * A password field with a button that shows or hides the password.
 *
 * @example
 * <PasswordInput
 *     showPasswordLabel={t('form.password.show')}
 *     hidePasswordLabel={t('form.password.hide')}
 *     {...register('password')}
 * />
 */
export function PasswordInput({ showPasswordLabel, hidePasswordLabel, ...props }: PasswordInputProps): ReactElement {
    const [isVisible, setIsVisible] = useState(false);

    return (
        <Input
            type={isVisible ? 'text' : 'password'}
            right={
                <button
                    type="button"
                    className={styles.toggle}
                    onClick={() => setIsVisible((prev) => !prev)}
                    aria-label={isVisible ? hidePasswordLabel : showPasswordLabel}
                >
                    <Icon icon={isVisible ? EyeOff : Eye} />
                </button>
            }
            {...props}
        />
    );
}
